import React, { useEffect, useState } from 'react';
import MythLabsIcon from '../components/MythLabsIcon';
import Hero from '../components/Hero';
import CurrentProjects from '../components/CurrentProjects';
import TiktokPromo from '../components/TiktokPromo';
import About from '../components/About';
import SkillsStack from '../components/SkillsStack';
import Team from '../components/Team';
import Starluck from '../components/Starluck';
import Map from '../components/Map';
import NotificationPopup from '../components/NotificationPopup';
import CustomSelect from '../components/CustomSelect';
import { initScrollAnimations } from '../utils/scrollAnimation';
import '../App.css';

// Simple cookie helpers for rate limiting contact form submissions
const getContactSubmissionCookie = () => {
  const name = 'contact_submissions=';
  const decodedCookie = decodeURIComponent(document.cookie || '');
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(name) === 0) {
      try {
        return JSON.parse(c.substring(name.length));
      } catch (e) {
        return null;
      }
    }
  }
  return null;
};

const setContactSubmissionCookie = (data) => {
  const expires = new Date();
  // Keep cookie for 2 days to allow daily reset logic
  expires.setTime(expires.getTime() + 2 * 24 * 60 * 60 * 1000);
  document.cookie = `contact_submissions=${encodeURIComponent(
    JSON.stringify(data)
  )};expires=${expires.toUTCString()};path=/`;
};

function Home() {
  const [notification, setNotification] = useState({ isVisible: false, type: '', message: '' });
  const [requestType, setRequestType] = useState('');

  useEffect(() => {
    // Initialize scroll animations
    initScrollAnimations();

    // Center-mode services slider logic
    const track = document.getElementById('services-track');
    if (!track) return;

    const wrap = track.parentElement;
    const cards = Array.from(track.children);
    const dotsBox = document.getElementById('services-dots');

    const isMobile = () => window.matchMedia('(max-width:767px)').matches;

    // Hide dots - they're not needed for desktop with few cards
    dotsBox.hidden = true;
    const dots = [];

    let current = 0;

    function center(i) {
      const card = cards[i];
      const axis = isMobile() ? 'top' : 'left';
      const size = isMobile() ? 'clientHeight' : 'clientWidth';
      const start = isMobile() ? card.offsetTop : card.offsetLeft;
      wrap.scrollTo({
        [axis]: start - (wrap[size] / 2 - card[size] / 2),
        behavior: 'smooth',
      });
    }

    function toggleUI(i) {
      cards.forEach((c, k) => c.toggleAttribute('active', k === i));
      if (dots.length > 0) {
        dots.forEach((d, k) => d.classList.toggle('active', k === i));
      }
    }

    function activate(i, scroll) {
      if (i === current) return;
      current = i;
      toggleUI(i);
      if (scroll) center(i);
    }

    function go(step) {
      activate(
        Math.min(Math.max(current + step, 0), cards.length - 1),
        true
      );
    }

    const keyHandler = (e) => {
      if (['ArrowRight', 'ArrowDown'].includes(e.key)) go(1);
      if (['ArrowLeft', 'ArrowUp'].includes(e.key)) go(-1);
    };

    window.addEventListener('keydown', keyHandler, { passive: true });

    cards.forEach((card, i) => {
      card.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover:hover)').matches) activate(i, true);
      });
      card.addEventListener('click', () => activate(i, true));
    });

    let sx = 0;
    let sy = 0;

    const touchStart = (e) => {
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
    };

    const touchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      if (isMobile() ? Math.abs(dy) > 60 : Math.abs(dx) > 60) {
        go((isMobile() ? dy : dx) > 0 ? -1 : 1);
      }
    };

    track.addEventListener('touchstart', touchStart, { passive: true });
    track.addEventListener('touchend', touchEnd, { passive: true });


    const resizeHandler = () => center(current);
    window.addEventListener('resize', resizeHandler);

    toggleUI(0);
    center(0);

    return () => {
      window.removeEventListener('keydown', keyHandler);
      window.removeEventListener('resize', resizeHandler);
      track.removeEventListener('touchstart', touchStart);
      track.removeEventListener('touchend', touchEnd);
    };
  }, []);

  return (
    <main>
      <Hero />

      <section className="services scroll-fade-in" id="services">
        <div className="head">
          <h2>Services we provide</h2>
        </div>

        <div className="slider">
          <div className="track" id="services-track">
            <article className="project-card" active="true" style={{ backgroundImage: 'url(/WebsitePhotos/consulting.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="project-card__content">
                <div>
                  <h3 className="project-card__title">Consulting</h3>
                  <p className="project-card__desc">
                    Product, UX and technical strategy to help you choose the
                    right problems, not just the right tools.
                  </p>
                </div>
              </div>
            </article>

            <article className="project-card" style={{ backgroundImage: 'url(/WebsitePhotos/app_development.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="project-card__content">
                <div>
                  <h3 className="project-card__title">App Development</h3>
                  <p className="project-card__desc">
                    Full‑stack web and mobile apps, from prototypes to
                    production systems your team can maintain.
                  </p>
                </div>
              </div>
            </article>

            <article className="project-card" style={{ backgroundImage: 'url(/WebsitePhotos/graphic_design.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="project-card__content">
                <div>
                  <h3 className="project-card__title">Graphic Design</h3>
                  <p className="project-card__desc">
                    Visual identities, pitch decks and marketing assets that
                    make your brand feel cohesive and unmistakable.
                  </p>
                </div>
              </div>
            </article>

            <article className="project-card" style={{ backgroundImage: 'url(/WebsitePhotos/it_support.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="project-card__content">
                <div>
                  <h3 className="project-card__title">IT Support</h3>
                  <p className="project-card__desc">
                    Secure, dependable infrastructure, monitoring and
                    hands‑on help so your stack quietly stays online.
                  </p>
                </div>
              </div>
            </article>

            <article className="project-card" style={{ backgroundImage: 'url(/WebsitePhotos/cloud_computing.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="project-card__content">
                <div>
                  <h3 className="project-card__title">Cloud & Automation</h3>
                  <p className="project-card__desc">
                    CI/CD, cloud infrastructure and integrations that turn
                    manual workflows into push‑button deployments.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>

        <div className="dots" id="services-dots" />
      </section>

      <About />

      <SkillsStack />

      <CurrentProjects />

      <Team />

      <Starluck />

      <TiktokPromo />

      <About />

      <NotificationPopup
        type={notification.type}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={() => setNotification({ ...notification, isVisible: false })}
      />

      <section className="contact-us scroll-fade-in" id="contact-form">
        <div className="contact-container">
          <div className="contact-card contact-form-card">
            <h3>Contact Us</h3>
            <p>Feel Free to contact us any time. We will get back to you as soon as we can!</p>

            <form
              id="form"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const submitBtn = form.querySelector('button[type="submit"]');
                const hCaptchaField = form.querySelector('textarea[name="h-captcha-response"]');
                const hCaptchaValue = hCaptchaField ? hCaptchaField.value : '';

                // Rate limit: max 2 messages per day (per browser via cookie)
                const today = new Date().toISOString().slice(0, 10);
                const submissionInfo = getContactSubmissionCookie();
                const currentCount =
                  submissionInfo && submissionInfo.date === today
                    ? submissionInfo.count
                    : 0;
                if (currentCount >= 2) {
                  setNotification({ isVisible: true, type: 'error', message: 'Message limit reached for today (2 per day).' });
                  return;
                }

                if (!hCaptchaValue) {
                  setNotification({ isVisible: true, type: 'error', message: 'Please complete the captcha.' });
                  return;
                }
                const formData = new FormData(form);
                formData.append("access_key", "3cbdc37a-af1e-4c17-8a2f-68073d5baa95");

                const originalText = submitBtn.textContent;
                submitBtn.textContent = "Sending...";
                submitBtn.disabled = true;

                try {
                  const response = await fetch("https://api.web3forms.com/submit", {
                    method: "POST",
                    body: formData
                  });

                  const data = await response.json();

                  if (response.ok) {
                    setNotification({ isVisible: true, type: 'success', message: 'Success! Your message has been sent.' });
                    form.reset();
                    // increment count on success
                    const updatedCount = currentCount + 1;
                    setContactSubmissionCookie({ date: today, count: updatedCount });
                  } else {
                    setNotification({ isVisible: true, type: 'error', message: 'Error: ' + (data.message || 'Something went wrong') });
                  }
                } catch (error) {
                  setNotification({ isVisible: true, type: 'error', message: 'Something went wrong. Please try again.' });
                } finally {
                  submitBtn.textContent = originalText;
                  submitBtn.disabled = false;
                }
              }}
              className="contact-form"
            >
              <label className="contact-label">
                <span>Name</span>
                <input type="text" name="name" placeholder="Name" required />
              </label>
              <label className="contact-label">
                <span>Email</span>
                <input type="email" name="email" placeholder="Email" required />
              </label>
              <label className="contact-label">
                <span>Type of Request</span>
                <CustomSelect
                  name="request_type"
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  placeholder="Select a request type"
                  required
                  options={[
                    { value: 'collaboration', label: 'Collaboration' },
                    { value: 'project', label: 'Project Inquiry' },
                    { value: 'consultation', label: 'Consultation' },
                    { value: 'other', label: 'Other' }
                  ]}
                />
              </label>
              <label className="contact-label">
                <span>Message</span>
                <textarea name="message" placeholder="Message" rows="4" required />
              </label>
              <div
                className="h-captcha"
                data-captcha="true"
                data-sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
                data-theme="light"
              ></div>
              <button type="submit" className="contact-form-submit">Send</button>
            </form>
          </div>

          <div className="contact-card contact-info-card">
            <h4>Contact Info</h4>
            <ul className="contact-info-list">
              <li>
                <i className="fas fa-envelope-open-text"></i>
                <div>
                  <span className="label">Email</span>
                  <span className="value">
                    <a href="mailto:contact@mythlabs.org" className="developer-email">contact@mythlabs.org</a>
                  </span>
                </div>
              </li>
              <li>
                <i className="fas fa-map-marked-alt"></i>
                <div>
                  <span className="label">Location</span>
                  <span className="value">Giarmata Strada Viilor 63</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Map />

      <footer className="footer">
        <div className="bubbles">
          {Array.from({ length: 128 }).map((_, i) => {
            const size = 2 + Math.random() * 4;
            const distance = 6 + Math.random() * 4;
            const position = -5 + Math.random() * 110;
            const time = 4 + Math.random() * 3;
            const delay = -1 * (3 + Math.random() * 3);
            return (
              <div
                key={i}
                className="bubble"
                style={{
                  '--size': `${size}rem`,
                  '--distance': `${distance}rem`,
                  '--position': `${position}%`,
                  '--time': `${time}s`,
                  '--delay': `${delay}s`
                }}
              />
            );
          })}
        </div>
        <div className="content">
          <div>
            <div className="footer-brand">
              <MythLabsIcon className="footer-icon" />
              <span className="footer-brand-text">MythLabs</span>
            </div>
          </div>
        </div>
        <div className="footer-copyright">
          <p>©{new Date().getFullYear()} MythLabs</p>
        </div>
      </footer>
    </main>
  );
}

export default Home;

